// BaseRail.jsx

import React, { useCallback, useState } from 'react';

// Context
import { useStudio } from 'context/StudioContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Services
import { getBaseKey } from 'services/bases/BaseReadService';

// Hooks
import useUnreadNotificationsByBase from 'hooks/useUnreadNotificationsByBase';
import useBaseExpiredBadges from 'hooks/useBaseExpiredBadges';
import useBaseRailOrder from 'hooks/useBaseRailOrder';
import useBaseSwitcher from 'hooks/useBaseSwitcher';
import useBaseVocabulary from 'hooks/useBaseVocabulary';

// CSS
import './BaseRail.css';

// Components
import Button from 'components/button/Button';
import BaseRailIcon from 'main/base-rail/BaseRailIcon';
import BaseAddModal from 'bases/add/BaseAddModal';

/**
 * BaseRail
 *
 * Desktop base switcher column (Slack-style rail).
 *
 * @returns {ReactNode} The rendered component.
 */
const BaseRail = React.memo(() => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();
    const { baseLabelPlural } = useBaseVocabulary();
    const { isStudioOpen } = useStudio();

    // ----- CONTEXT ----- //
    const { userUnreadNotifications } = useUser();

    // ----- HOOKS ----- //
    const {
        activeBaseKey,
        addOpen,
        handleBaseAdd,
        handleBaseSelect,
        setAddOpen,
        user,
        bases,
    } = useBaseSwitcher();

    const unreadByBaseKey = useUnreadNotificationsByBase(userUnreadNotifications);
    const expiredByKey = useBaseExpiredBadges(bases);
    const {
        canReorder,
        orderedBases,
        reorderBases,
    } = useBaseRailOrder(bases);

    // ----- STATE ----- //
    const [draggingBaseKey, setDraggingBaseKey] = useState('');
    const [dragOverBaseKey, setDragOverBaseKey] = useState('');

    // ----- HANDLERS ----- //
    const handleDragStart = useCallback((baseKey, event) => {
        if (!canReorder || !baseKey) return;

        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', baseKey);
        setDraggingBaseKey(baseKey);
        setDragOverBaseKey(baseKey);
    }, [canReorder]);

    const handleDragOver = useCallback((baseKey, event) => {
        if (!canReorder || !draggingBaseKey || !baseKey || draggingBaseKey === baseKey) {
            return;
        }

        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';

        if (dragOverBaseKey !== baseKey) {
            setDragOverBaseKey(baseKey);
        }
    }, [canReorder, dragOverBaseKey, draggingBaseKey]);

    const handleDragEnd = useCallback(() => {
        setDraggingBaseKey('');
        setDragOverBaseKey('');
    }, []);

    const handleDrop = useCallback(async (baseKey, event) => {
        if (!canReorder || !draggingBaseKey || !baseKey || draggingBaseKey === baseKey) {
            handleDragEnd();
            return;
        }

        event.preventDefault();
        await reorderBases(draggingBaseKey, baseKey);
        handleDragEnd();
    }, [canReorder, draggingBaseKey, handleDragEnd, reorderBases]);

    if (!user) return null;

    // ----- RENDER ----- //

    return (
        <>
            <nav
              className="base-rail-container"
              aria-label={baseLabelPlural || 'Bases'}
            >

                {/* ICONS */}
                <div className="base-rail-icons">
                    {orderedBases.map((base) => {
                        const baseKey = getBaseKey(base);
                        const isActive = !isStudioOpen
                            && Boolean(activeBaseKey && baseKey === activeBaseKey);
                        const isExpired = baseKey ? expiredByKey.get(baseKey) === true : false;
                        const unreadCount = baseKey ? (unreadByBaseKey.get(baseKey) || 0) : 0;

                        return (
                            <BaseRailIcon
                              key={baseKey || String(base?.title || '')}
                              base={base}
                              isActive={isActive}
                              isDragging={draggingBaseKey === baseKey}
                              isDropTarget={dragOverBaseKey === baseKey && draggingBaseKey !== baseKey}
                              isExpired={isExpired}
                              isReorderEnabled={canReorder}
                              unreadCount={unreadCount}
                              onClick={() => handleBaseSelect(base)}
                              onDragEnd={handleDragEnd}
                              onDragOver={(event) => handleDragOver(baseKey, event)}
                              onDragStart={(event) => handleDragStart(baseKey, event)}
                              onDrop={(event) => handleDrop(baseKey, event)}
                            />
                        );
                    })}

                    <Button
                      iconKey="add"
                      iconColor={theme.foregroundMuted}
                      iconSize={36}
                      width="40px"
                      height="40px"
                      padding={0}
                      borderRadius="9px"
                      backgroundColor="transparent"
                      hoverBackgroundColor="transparent"
                      borderColor="transparent"
                      title="New Workspace"
                      onClick={handleBaseAdd}
                    />

                </div>
            </nav>

            <BaseAddModal
              isOpen={addOpen}
              setOpen={setAddOpen}
              onSave={(base) => {
                    if (base?.key) {
                        handleBaseSelect(base);
                    }
                }}
            />
        </>
    );
});

// Dev Tools
BaseRail.displayName = 'BaseRail';

export default BaseRail;
